import BaseApiClient from "../api/baseApiClient.service";

/**
 * Represents an organization in the platform
 */
export interface Organization {
	id: string;
	name: string;
	description?: string;
	slug?: string;
	logoUrl?: string;
	enabled?: boolean;
	isDefault?: boolean;
	licensedUsers?: number | null; // a soft limit for the number of users that can be added to the organization
	userCount?: number; // this is a property that is not part of the model itself, but is calculated by the backend
	canManageOwnContent?: boolean; // whether organization admins can create modules
}

/**
 * Request body for creating or updating an organization
 */
export interface CreateOrUpdateOrganizationRequest {
	name: string;
	description?: string;
}

/**
 * Service for managing platform-related operations
 */
class PlatformManagerOrganizationsService {
	private static instance: PlatformManagerOrganizationsService;
	private baseApi: BaseApiClient;

	private constructor() {
		this.baseApi = BaseApiClient.getInstance();
	}

	public static getInstance(): PlatformManagerOrganizationsService {
		if (!PlatformManagerOrganizationsService.instance) {
			PlatformManagerOrganizationsService.instance =
				new PlatformManagerOrganizationsService();
		}
		return PlatformManagerOrganizationsService.instance;
	}

	/**
	 * Fetches all organizations
	 * @returns Promise with all organizations (with user count)
	 */
	public async getAllOrganizations(): Promise<Organization[]> {
		return this.baseApi.get<Organization[]>("/platform-manager/organizations");
	}
	/**
	 * Creates a new organization
	 * @param data Organization data
	 * @returns Promise with the created organization
	 */
	public async createOrganization(
		data: CreateOrUpdateOrganizationRequest,
	): Promise<Organization> {
		return this.baseApi.post<Organization, CreateOrUpdateOrganizationRequest>(
			"/platform-manager/organizations",
			data,
		);
	}

	/**
	 * Updates an organization
	 * @param id Organization ID
	 * @param data Organization data
	 * @returns Promise with the updated organization
	 */
	public async updateOrganization(
		id: string,
		data: Partial<CreateOrUpdateOrganizationRequest>,
	): Promise<Organization> {
		return this.baseApi.put<
			Organization,
			Partial<CreateOrUpdateOrganizationRequest>
		>(`/platform-manager/organizations/${id}`, data);
	}

	/**
	 * Resets the taxpeer organization test data
	 * @returns Promise with success status
	 */
	public async resetTestData(): Promise<void> {
		return this.baseApi.get<void>(
			"/platform-manager/testing/reset-taxpeer-orga-exams",
		);
	}
}

export default PlatformManagerOrganizationsService;
